import React, { useEffect, useState } from "react";
import { Settings, Save, Loader2, BarChart3, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Switch } from "../../components/ui/switch";

export default function NewsAutomationSettings() {
    const [config, setConfig] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: c }, { data: s }] = await Promise.all([
                api.get("/admin/news-automation/config"),
                api.get("/admin/news-automation/stats"),
            ]);
            setConfig(c);
            setStats(s);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updateField = (key, value) => setConfig((p) => ({ ...p, [key]: value }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                relevance_threshold: parseInt(config.relevance_threshold, 10) || 60,
                keywords_must_include: config.keywords_must_include || [],
                keywords_exclude: config.keywords_exclude || [],
                max_drafts_per_day: parseInt(config.max_drafts_per_day, 10) || 10,
                fetch_enabled: !!config.fetch_enabled,
                process_enabled: !!config.process_enabled,
                max_attempts: parseInt(config.max_attempts, 10) || 3,
                digest_auto_generate: !!config.digest_auto_generate,
                digest_day: config.digest_day || "sun",
                digest_hour: parseInt(config.digest_hour, 10) || 6,
                ai_provider: config.ai_provider || "anthropic",
                ai_model: config.ai_model || "claude-sonnet-4-5-20250929",
            };
            await api.put("/admin/news-automation/config", payload);
            toast.success("Ayarlar kaydedildi.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const generateDigestNow = async () => {
        if (!window.confirm("Son 7 günün yazılarından bülten taslağı oluşturulsun mu?")) return;
        try {
            const { data } = await api.post("/admin/news-automation/digest/generate-now");
            if (data.ok) toast.success(`Bülten taslağı oluşturuldu (${data.post_count} yazı)`);
            else toast.warning(`Atlandı: ${data.skipped || "—"}`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    if (loading || !config) {
        return (
            <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
            </div>
        );
    }

    return (
        <div data-testid="admin-news-settings-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <Settings className="w-6 h-6 text-teal" /> Haber Otomasyon Ayarları
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">
                        RSS çekim + AI işleme parametreleri. Scheduler 6 saatte bir çalışır (UTC).
                    </p>
                </div>
                <button
                    onClick={save}
                    disabled={saving}
                    className="bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold px-5 py-2 rounded-md inline-flex items-center gap-2"
                    data-testid="news-settings-save"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Kaydet
                </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
                <Card title="Otomatik Akış">
                    <div className="space-y-3">
                        <ToggleRow
                            label="RSS Otomatik Çekim"
                            help="6 saatte bir tüm aktif RSS kaynakları taranır."
                            checked={config.fetch_enabled}
                            onChange={(v) => updateField("fetch_enabled", v)}
                            testId="news-cfg-fetch-enabled"
                        />
                        <ToggleRow
                            label="AI Otomatik İşleme"
                            help="Çekilen ham haberler AI ile relevance skor + çeviri yapılarak taslağa dönüştürülür."
                            checked={config.process_enabled}
                            onChange={(v) => updateField("process_enabled", v)}
                            testId="news-cfg-process-enabled"
                        />
                    </div>
                    {(!config.fetch_enabled || !config.process_enabled) && (
                        <div className="mt-3 p-3 rounded-md bg-amber-50 border-l-4 border-amber-400 flex items-start gap-2 text-xs text-amber-800">
                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>Otomatik akış kapalıyken manuel "Tümünü Şimdi Çek" + "AI Toplu İşle" butonları kullanılabilir.</span>
                        </div>
                    )}
                </Card>

                <Card title="Filtreleme Parametreleri">
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">
                                Relevance Eşiği: {config.relevance_threshold}/100
                            </label>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={config.relevance_threshold}
                                onChange={(e) => updateField("relevance_threshold", parseInt(e.target.value, 10))}
                                className="mt-2 w-full accent-teal"
                                data-testid="news-cfg-threshold"
                            />
                            <div className="flex justify-between text-[10px] text-deep-blue/60 mt-1">
                                <span>0 (her şey)</span><span>60 (varsayılan)</span><span>100 (sadece tam isabet)</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">İçermesi Gereken Kelimeler</label>
                            <input
                                value={(config.keywords_must_include || []).join(", ")}
                                onChange={(e) => updateField("keywords_must_include", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                placeholder="otel, turizm, konaklama"
                                data-testid="news-cfg-must-include"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Hariç Tutulacak Kelimeler</label>
                            <input
                                value={(config.keywords_exclude || []).join(", ")}
                                onChange={(e) => updateField("keywords_exclude", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                placeholder="siyaset, magazin"
                                data-testid="news-cfg-exclude"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Günlük Max Taslak</label>
                                <input
                                    type="number"
                                    value={config.max_drafts_per_day}
                                    onChange={(e) => updateField("max_drafts_per_day", parseInt(e.target.value, 10) || 10)}
                                    min={1}
                                    max={200}
                                    className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-data"
                                    data-testid="news-cfg-max-drafts"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Max Deneme</label>
                                <input
                                    type="number"
                                    value={config.max_attempts}
                                    onChange={(e) => updateField("max_attempts", parseInt(e.target.value, 10) || 3)}
                                    min={1}
                                    max={10}
                                    className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-data"
                                />
                            </div>
                        </div>
                    </div>
                </Card>

                <Card title="AI Sağlayıcı">
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Sağlayıcı</label>
                            <select
                                value={config.ai_provider || "anthropic"}
                                onChange={(e) => updateField("ai_provider", e.target.value)}
                                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white"
                                data-testid="news-cfg-ai-provider"
                            >
                                <option value="anthropic">Anthropic (Claude)</option>
                                <option value="openai">OpenAI (GPT)</option>
                                <option value="gemini">Google (Gemini)</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Model Adı</label>
                            <input
                                value={config.ai_model || "claude-sonnet-4-5-20250929"}
                                onChange={(e) => updateField("ai_model", e.target.value)}
                                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono"
                                placeholder="claude-sonnet-4-5-20250929"
                                data-testid="news-cfg-ai-model"
                            />
                            <p className="mt-1 text-[11px] text-deep-blue/60">
                                Önerilen: <code>claude-sonnet-4-5-20250929</code> (Anthropic), <code>gpt-5</code> (OpenAI), <code>gemini-2.5-pro</code> (Gemini). Emergent Universal Key bu üçünü destekler.
                            </p>
                        </div>
                    </div>
                </Card>

                <Card title="Haftalık Bülten" full>
                    <div className="grid sm:grid-cols-3 gap-3 mb-3">
                        <ToggleRow
                            label="Otomatik Bülten Oluştur"
                            help="Her haftanın belirlenen gününde son 7 günün yazılarından AI bülten taslağı."
                            checked={config.digest_auto_generate}
                            onChange={(v) => updateField("digest_auto_generate", v)}
                            testId="news-cfg-digest-enabled"
                        />
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Gün</label>
                            <select
                                value={config.digest_day || "sun"}
                                onChange={(e) => updateField("digest_day", e.target.value)}
                                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white"
                            >
                                <option value="mon">Pazartesi</option>
                                <option value="tue">Salı</option>
                                <option value="wed">Çarşamba</option>
                                <option value="thu">Perşembe</option>
                                <option value="fri">Cuma</option>
                                <option value="sat">Cumartesi</option>
                                <option value="sun">Pazar</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Saat (UTC)</label>
                            <input
                                type="number"
                                value={config.digest_hour ?? 6}
                                onChange={(e) => updateField("digest_hour", parseInt(e.target.value, 10) || 0)}
                                min={0}
                                max={23}
                                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-data"
                            />
                        </div>
                    </div>
                    <button
                        onClick={generateDigestNow}
                        className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2"
                        data-testid="news-cfg-digest-now"
                    >
                        Şimdi Bülten Oluştur
                    </button>
                    <p className="mt-2 text-xs text-deep-blue/60">
                        Son 7 günün yayınlanmış blog yazılarından AI ile bülten taslağı üretir, <strong>/admin/bultenler</strong>'de "Bültenler" listesinde görünür.
                    </p>
                </Card>

                {stats && (
                    <Card title="İstatistikler" full>
                        <div className="grid sm:grid-cols-4 gap-3 mb-4">
                            <Stat label="Aktif Kaynak" value={`${stats.active_sources} / ${stats.sources_count}`} />
                            <Stat label="Toplam Ham Haber" value={stats.raw_total} />
                            <Stat label="İncelemede Taslak" value={stats.drafts_pending_review} accent="amber" />
                            <Stat label="Yayında" value={stats.drafts_published} accent="green" />
                        </div>
                        {stats.source_stats && stats.source_stats.length > 0 && (
                            <div>
                                <h3 className="text-xs font-bold uppercase tracking-wider text-deep-blue mb-2">Kaynak Performansı</h3>
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-wider text-deep-blue/60">
                                            <th className="py-2">Kaynak</th>
                                            <th className="py-2 text-right">Toplam</th>
                                            <th className="py-2 text-right">Taslak</th>
                                            <th className="py-2 text-right">Reddedildi</th>
                                            <th className="py-2 text-right">Başarı %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.source_stats.map((s) => (
                                            <tr key={s.id} className="border-t border-slate-100">
                                                <td className="py-2 font-semibold text-dark-navy">{s.name}</td>
                                                <td className="py-2 text-right font-data">{s.total_fetched}</td>
                                                <td className="py-2 text-right font-data text-data-green">{s.draft_created}</td>
                                                <td className="py-2 text-right font-data text-data-red">{s.rejected}</td>
                                                <td className="py-2 text-right font-data font-bold">{s.success_rate}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
}

function Card({ title, children, full }) {
    return (
        <div className={`bg-white rounded-lg shadow-md border border-slate-100 p-5 ${full ? "lg:col-span-2" : ""}`}>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-4 inline-flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> {title}
            </h2>
            {children}
        </div>
    );
}

function ToggleRow({ label, help, checked, onChange, testId }) {
    return (
        <div className="flex items-start justify-between gap-3 bg-light-bg rounded-md p-3">
            <div className="flex-1">
                <div className="text-sm font-semibold text-dark-navy">{label}</div>
                <p className="text-xs text-deep-blue/70 mt-0.5">{help}</p>
            </div>
            <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
        </div>
    );
}

function Stat({ label, value, accent = "navy" }) {
    const color = accent === "green" ? "text-data-green" : accent === "amber" ? "text-amber-600" : "text-dark-navy";
    return (
        <div className="bg-light-bg rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-deep-blue/70 font-semibold">{label}</div>
            <div className={`mt-1 font-data text-xl font-bold ${color}`}>{value}</div>
        </div>
    );
}
